import { createSlice } from '@reduxjs/toolkit';

// Firebase
import firebase from 'firebase/app';
import 'firebase/analytics';

const DEFAULT_PIXEL_SIZE = 1920;
const MAX_PIXEL_SIZE = 20000;

export const collageSlice = createSlice({
  name: 'collage',
  initialState: {
    needRender: true,
    photos: {
      num: 0,
      numChosen: 0,
      nodes: {},
      chosenNodes: {}
    },
    mode: window.innerWidth < 1024 ? 'sm' : 'lg',
    texts: [],
    activeEditScreen: 'main',
    activeText: false,
    activeBlock: null,
    hologramPosition: null,
    swap: [],
    shuffle: false,
    layout: 'Gandr',
    borderWidth: 0,
    backgroundColor: '#FFFFFF',
    pixelSize: DEFAULT_PIXEL_SIZE,
    maxPixelSize: MAX_PIXEL_SIZE,
    resolution: {
      width: DEFAULT_PIXEL_SIZE,
      height: DEFAULT_PIXEL_SIZE,
      ratio: {
        name: '1:1',
        multiplierHeight: 1,
        multiplierWidth: 1,
        isPortrait: false,
        split: {
          w: 1,
          h: 1
        }
      }
    }
  },
  reducers: {
    acknowledgeRender: (state, action) => {
      state.needRender  = false;
      state.shuffle     = false;
    },
    setMode: (state, action) => {
      state.mode = action.payload;

      if ( state.mode == 'lg' && state.activeEditScreen == 'text_options' ) {
        state.activeEditScreen = 'main';
      }
    },
    setResolution: (state, action) => {

      let args = action.payload;
      let newResolutionWidth;
      let newResolutionHeight;
      let ratioWidth;
      let ratioHeight;
      // let ratioMultiplierWidth  = (ratioHeight / ratioWidth);
      // let ratioMultiplierHeight = (ratioWidth / ratioHeight);

      if ( args.ratio ) {
        let ratioSplit      = args.ratio.split(':');
        ratioWidth          = parseInt(ratioSplit[0]);
        ratioHeight         = parseInt(ratioSplit[1]);
        newResolutionWidth  = state.pixelSize;
        newResolutionHeight = state.pixelSize;
      }
      else {
        ratioWidth          = parseInt(args.width);
        ratioHeight         = parseInt(args.height);
        newResolutionWidth  = ratioWidth;
        newResolutionHeight = ratioHeight;
      }

      let ratioMultiplierHeight = (ratioWidth / ratioHeight);
      let ratioMultiplierWidth  = (ratioHeight / ratioWidth);

      state.resolution.ratio = {
        name: args.ratio ? args.ratio : 'Custom',
        multiplierHeight: ratioMultiplierHeight,
        multiplierWidth: ratioMultiplierWidth,
        isPortrait: ratioHeight > ratioWidth,
        split: {
          w: ratioWidth,
          h: ratioHeight
        }
      }

      if ( args.ratio ) {
        if ( state.resolution.ratio.isPortrait ) {
          state.resolution.width = Math.floor(newResolutionWidth * state.resolution.ratio.multiplierHeight);
          state.resolution.height = newResolutionHeight;
        }
        else {
          state.resolution.width = newResolutionWidth;
          state.resolution.height = Math.floor(newResolutionHeight * state.resolution.ratio.multiplierWidth);
        }
      } else {

        // More security measures to make sure we're not beyond max size
        if ( newResolutionWidth > state.maxPixelSize ) 
          newResolutionWidth =  state.maxPixelSize;

        if ( newResolutionHeight > state.maxPixelSize ) 
          newResolutionHeight =  state.maxPixelSize;

        state.resolution.width  = newResolutionWidth;
        state.resolution.height = newResolutionHeight;
        
      }

      state.needRender = 0;

      firebase.analytics().logEvent('set_resolution');

    },
    setLayout: (state, action) => {
      state.layout = action.payload;
      state.needRender = 0;
      firebase.analytics().logEvent('set_layout');
    },
    setBorderWidth: (state, action) => {
      state.borderWidth = action.payload;
    },
    setBackgroundColor: (state, action) => {
      state.backgroundColor = action.payload;
      firebase.analytics().logEvent('set_background');
    },
    shuffle: (state, action) => {
      state.needRender  = 0;
      state.shuffle     = true;
      state.activeBlock = null;
      state.swap        = [];
      firebase.analytics().logEvent('shuffle');
    },
    setActiveBlock: (state, action) => {
      state.activeBlock = action.payload;
    },
    setHologramPosition: (state, action) => {
      state.hologramPosition = action.payload;
    },
    addToSwap: (state, action) => {
      if ( state.swap.length === 0 ) {
        state.swap[0] = action.payload;
      }
      else if ( state.swap.length === 1 ) {
        if ( action.payload != state.swap[0] ) {
          state.activeBlock = null;
          state.swap[1] = action.payload;
        }
        else {
          state.swap = [];
        }
      }
      else {
        state.swap = [];
      }
    },
    acknowledgeSwap: (state, action) => {
      state.swap = [];
    },
    addTextBlock: (state, action) => {
      state.texts.push({
        text: 'Double Click To Edit Me',
        fontSize: 26,
        fontWeight: 400,
        fontStyle: 'normal',
        textDecoration: 'none',
        color: '#000000',
        positioning: {
          width: 305,
          height: 55,
          left: '20%',
          top: '20%',
          rotate: '0deg'
        }
      });
      state.activeText = (state.texts.length - 1);
      if ( state.mode == 'sm' ) {
        state.activeEditScreen  = 'text_options';
      }
      firebase.analytics().logEvent('add_text_block');
    },
    removeTextBlock: (state, action) => {
      state.activeText = false;
      state.activeEditScreen = 'main';
      delete state.texts[action.payload];
    },
    setActiveText: (state, action) => {
      state.activeText        = action.payload;
      if ( state.mode == 'sm' ) {
        state.activeEditScreen  = 'text_options';
      }
    },
    setTextSize: (state, action) => {
      state.texts[state.activeText].fontSize = parseInt(action.payload);
    },
    setTextBold: (state, action) => {
      state.texts[state.activeText].fontWeight = state.texts[state.activeText].fontWeight == 700 ? 400 : 700;
    },
    setTextItalic: (state, action) => {
      state.texts[state.activeText].fontStyle = state.texts[state.activeText].fontStyle == 'normal' ? 'italic' : 'normal';
    },
    setTextUnderline: (state, action) => {
      state.texts[state.activeText].textDecoration = state.texts[state.activeText].textDecoration == 'none' ? 'underline' : 'none';
    },
    setTextText: (state, action) => {
      state.texts[state.activeText].text = action.payload.replace(/(?:\r\n|\r|\n)/g, '<br>');;
    },
    setTextColour: (state, action) => {
      state.texts[state.activeText].color = action.payload;
    },
    setTextPositioning: (state, action) => {
      state.texts[state.activeText].positioning = action.payload;
    },
    clickAway: (state, action) => {

      let args   = action.payload;

      if ( args.unhighlightText ) {
        state.activeText = false;
        state.activeEditScreen = 'main';
      }

      // If DIDN'T click block, the active block can be made un-active
      if ( args.removeActiveBlock ) state.activeBlock = null;

      // If DIDN'T click a hologram option, then hide the hologram menu
      if ( args.resetHologram ) state.hologramPosition = null;

    },
    setActiveEditScreen: (state, action) => {
      state.activeEditScreen = action.payload;
    },

    // Photo related
    bulkAddPhotosToPool: (state, action) => {
      Object.keys(action.payload).map(src => {
        state.photos.nodes[src] = processPhotoPoolObject(action.payload[src]);
      });
      state.needRender          = 100;
      state.hologramPosition    = null;
      state.swap                = [];
      // state.photos.chosenNodes  = state.photos.nodes;
      state.photos.num          = Object.keys(state.photos.nodes).length;
      // state.photos.numChosen    = Object.keys(state.photos.chosenNodes).length;
    },
    addPhotoToPool: (state, action) => {
      state.photos.nodes[action.payload.src] = processPhotoPoolObject(action.payload);
      state.photos.num = Object.keys(state.photos.nodes).length;
    },
    chooseAllPhotos: (state, action) => {
      state.needRender          = 100;
      state.hologramPosition    = null;
      state.swap                = [];
      state.photos.chosenNodes  = state.photos.nodes;
      state.photos.numChosen    = Object.keys(state.photos.chosenNodes).length;
    },
    unchooseAllPhotos: (state, action) => {
      state.needRender          = 100;
      state.hologramPosition    = null;
      state.swap                = [];
      state.photos.chosenNodes  = {};
      state.activeBlock         = null;
      state.photos.numChosen    = Object.keys(state.photos.chosenNodes).length;
    },
    choosePhoto: (state, action) => {
      state.needRender        = getRenderDelay(Object.keys(state.photos.chosenNodes).length);
      state.hologramPosition  = null;
      state.swap              = [];
      state.photos.chosenNodes[action.payload.src] = action.payload.photo;
      state.photos.numChosen = Object.keys(state.photos.chosenNodes).length;
    },
    unchoosePhoto: (state, action) => {
      state.needRender        = getRenderDelay(Object.keys(state.photos.chosenNodes).length);
      state.hologramPosition  = null;
      state.swap              = [];
      state.activeBlock       = null;
      delete state.photos.chosenNodes[action.payload];
      state.photos.numChosen = Object.keys(state.photos.chosenNodes).length;
    }, 
  },
});

function getRenderDelay(numChosen) {

  let delay = 0;

  switch(true) {
    case numChosen > 50:
      delay = 2000;
      break;
    case numChosen > 30:
      delay = 1000;
      break;
    case numChosen > 15:
      // delay = 1000;
      delay = 100;
      break;
    default:
      delay = 100;
      // delay = 1000;
      break;
  }
console.log('render delay: ' + delay);
  return delay;

}

function processPhotoPoolObject(poolObj) {

  // state.needRender    = true;
  let isPortrait = (poolObj.height > poolObj.width);
  let ratioMultiplier = isPortrait ? (poolObj.height / poolObj.width) : (poolObj.width / poolObj.height);

  return  {
    isPortrait: isPortrait,
    ratio: ratioMultiplier,
    ...poolObj
  };

}

export const { acknowledgeRender, setMode, setResolution, setLayout, setBorderWidth, setBackgroundColor, shuffle, bulkAddPhotosToPool, addPhotoToPool, choosePhoto, chooseAllPhotos, unchoosePhoto, unchooseAllPhotos, setActiveBlock, setHologramPosition, addToSwap, acknowledgeSwap, addTextBlock, setActiveText, setTextSize, setTextBold, setTextItalic, setTextUnderline, setTextColour, setTextText, removeTextBlock, setTextPositioning, clickAway, setActiveEditScreen } = collageSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectCollageConfig = state => state.collageConfig;

export default collageSlice.reducer;
